import { BackgroundColor, type ThemeMode } from "@/store/settings-state"

export type ResolvedTheme = "light" | "dark"

const DARK_CLASS_NAME = "dark"
const LIGHT_CLASS_NAME = "light"
const SETTINGS_STORAGE_KEY = "settings"
const SYSTEM_DARK_QUERY = "(prefers-color-scheme: dark)"

const isThemeMode = (value: unknown): value is ThemeMode => {
	return value === "light" || value === "dark" || value === "system"
}

const isRecord = (value: unknown): value is Record<string, unknown> => {
	return typeof value === "object" && value !== null
}

export const getStoredThemeMode = (): ThemeMode => {
	if (typeof window === "undefined") {
		return "dark"
	}

	try {
		const rawSettings = window.localStorage.getItem(SETTINGS_STORAGE_KEY)
		if (!rawSettings) {
			return "dark"
		}

		const parsed: unknown = JSON.parse(rawSettings)
		if (!isRecord(parsed)) {
			return "dark"
		}
		return isThemeMode(parsed?.themeMode) ? parsed.themeMode : "dark"
	} catch {
		return "dark"
	}
}

export const resolveThemeMode = (themeMode: ThemeMode, isSystemDark?: boolean): ResolvedTheme => {
	if (themeMode === "system") {
		const resolvedSystemDark = isSystemDark ?? getSystemThemeMediaQuery()?.matches ?? false
		return resolvedSystemDark ? "dark" : "light"
	}
	return themeMode
}

export const applyThemeMode = (themeMode: ThemeMode, isSystemDark?: boolean): ResolvedTheme => {
	const resolvedTheme = resolveThemeMode(themeMode, isSystemDark)

	if (typeof document === "undefined") {
		return resolvedTheme
	}

	const root = document.documentElement
	root.classList.toggle(DARK_CLASS_NAME, resolvedTheme === "dark")
	root.classList.toggle(LIGHT_CLASS_NAME, resolvedTheme === "light")
	root.style.colorScheme = resolvedTheme

	return resolvedTheme
}

export const getSystemThemeMediaQuery = (): MediaQueryList | undefined => {
	if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
		return undefined
	}

	return window.matchMedia(SYSTEM_DARK_QUERY)
}

export const getEditorBackgroundForTheme = (theme: ResolvedTheme): BackgroundColor => {
	return theme === "dark" ? BackgroundColor.black : BackgroundColor.white
}
